import React from 'react';
import Chart from 'chart.js';
import LogService from '../../services/log-service';
import DeviceService from '../../services/device-service';
import Logger from '../../toolkit/logger/Logger';
import moment from 'jalali-moment';
import findSensorUnitByType from '../../utils/unit';
import { decode } from '../../utils/html-entity';
import * as chartUtils from '../../utils/chartjs';

export interface HomeChartProps {
  refreshInterval?: number;
}

export interface HomeChartState {
  index: number;
  devices: any[];
  logs: any[];
}

export const DEFAULT_CHART_REFRESH_INTERVAL = 10000;

export default class HomeChart extends React.Component<HomeChartProps, HomeChartState> {

  state = {
    index: -1,
    devices: [],
    logs: []
  }

  private chart: Chart;
  private logService = new LogService();
  private deviceService = new DeviceService();
  private intervalFinished = true;
  private interval = null;

  render() {
    return (
      <>
        <div className="home-box">
          <span className="home-title chart-title">{this.renderGraphTitle()}</span>
          <div className="d-flex h-100">
            <canvas id="home-chart" style={{ marginTop: '1.5rem', minHeight: '400px', height: '100% !important' }}></canvas>
          </div>
        </div>
      </>
    );
  }

  componentDidMount = async () => {
    Logger.info('HomeChart::ComponentDidMount');
    let devices = [];
    this.chart = chartUtils.create({ id: 'home-chart', datasets: [], times: [] });
    try {
      devices = (await this.deviceService.pureFetch({
        count: 1000, page: 1,
        sort: 'log._id',
        sortOrder: 'asc'
      })).data;
      this.setState({ devices }, async () => {
        if (devices.length > 0) {
          await this.changeDevice(0);
          await this.provideIndexChangerInterval();
        }
      });
    }
    catch (e) {
    }
  }

  componentWillUnmount = async () => {
    clearInterval(this.interval);
  }

  renderGraphTitle = () => {
    Logger.info('HomeChart::RenderGraphTitle');
    const { devices, index } = this.state;
    if (devices.length === 0 || index === -1) {
      return 'گراف ';
    }
    return (
      <>
        <span>گراف امروز دستگاه </span>
        <span className="serial">{devices[index]['_id']}</span>
      </>
    );
  }

  private provideIndexChangerInterval = async () => {
    Logger.info('HomeChart::IntervalInit');
    const time = this.props.refreshInterval ?? DEFAULT_CHART_REFRESH_INTERVAL;
    this.interval = setInterval(this.intervalAction, time);
  }

  private intervalAction = async () => {
    if (!this.intervalFinished) {
      Logger.info('HomeChart::IntervalIgnore');
      return;
    }
    Logger.info('HomeChart::IntervalExec');
    this.intervalFinished = false;
    let { index, devices } = this.state;
    index++;
    if (index >= devices.length) {
      index = 0;
    }
    await this.changeDevice(index);
    await this.refreshLogs();
    this.intervalFinished = true;
  }

  private changeDevice = async (index: number) => {
    Logger.info('HomeChart::ChangeDevice');
    return new Promise(resolve => {
      this.setState({ index }, async () => {
        await this.refreshLogs();
        this.updateGraph();
        resolve(undefined);
      });
    })
  }

  private refreshLogs = async () => {
    Logger.info('HomeChart::RefreshLog');
    const { index, devices } = this.state;
    if (devices.length === 0 || index === -1) {
      return this.setState({ logs: [] });
    }
    const device = devices[index];

    const date = moment().locale('fa');
    const logs = await this.logService.findByDeviceId(device['_id'], {
      day: date.jDate(),
      month: date.jMonth() + 1,
      year: date.jYear()
    });
    return new Promise(resolve => this.setState({ logs }, resolve as any));
  }

  private updateGraph = () => {
    Logger.info('HomeChart::UpdateGraph');
    const { devices, index, logs } = this.state;
    const showMonth = false;
    const device = devices[index];
    // const logs = chartUtils.provideDatasetGaps(raw, 180, showMonth);
    const values = chartUtils.extractValuesFromLogs(logs);
    const times = chartUtils.extractTimesFromLogs(logs, showMonth);
    const disconnects = chartUtils.provideDisconnects(logs);
    const datasets = chartUtils.makeDatasets(device, {
      data: values,
      max: device.max1,
      min: device.min1,
      maxPhone: device.maxPhone,
      minPhone: device.minPhone,
      disconnects
    });

    const unit = decode(findSensorUnitByType(device.type));
    chartUtils.update(this.chart, { datasets: datasets, times, unit });
  }

}
